import { useRef, useEffect, useState } from 'react';
import { GOOGLE_MAPS_API_KEY } from '../lib/config';

export type AddressData = {
  formatted_address: string;
  lat: number;
  lng: number;
  address_components: any[];
  place_id: string;
};

interface AddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressPicked?: (address: AddressData) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

export default function AddressInput({ 
  value, 
  onChange, 
  onAddressPicked, 
  placeholder = "Address Line 1*",
  className,
  style
}: AddressInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    // Verificar se já carregou
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      setIsLoaded(true);
      return;
    }
    
    // Listener para capturar erros do Google Maps
    const handleError = (e: ErrorEvent) => {
      if (e.message && e.message.includes('google')) {
        setError(e.message);
      }
    };
    window.addEventListener('error', handleError);
    
    // Carregar script
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Callback global
      (window as any).initGoogleMaps = () => {
        setIsLoaded(true);
        setError(null);
      };
      
      script.onerror = () => {
        setError('Script failed to load');
      };
      
      document.head.appendChild(script);
    } else {
      // Script já existe, verificar se carregou
      if (typeof google !== 'undefined' && google.maps) {
        setIsLoaded(true);
      } else {
        // Script existe mas google não está pronto - esperar
        const checkGoogle = setInterval(() => {
          if (typeof google !== 'undefined' && google.maps && google.maps.places) {
            setIsLoaded(true);
            clearInterval(checkGoogle);
          }
        }, 100);
        // Timeout de 5 segundos
        setTimeout(() => {
          clearInterval(checkGoogle);
        }, 5000);
      }
    }
    
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);
  
  // Inicializar autocomplete quando carregado
  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;
    
    try {
      const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
        fields: ['address_components', 'geometry', 'formatted_address', 'place_id'],
        types: ['address']
      });
      
      autocompleteRef.current = autocomplete;
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (place?.formatted_address && inputRef.current) {
          inputRef.current.value = place.formatted_address;
          onChange(place.formatted_address);
          
          const location = place.geometry?.location;
          if (location && place.address_components && onAddressPicked) {
            onAddressPicked({
              formatted_address: place.formatted_address,
              lat: location.lat(),
              lng: location.lng(),
              address_components: place.address_components,
              place_id: place.place_id || ''
            });
          }
          
          // Esconder o dropdown após seleção (com delay para não interferir)
          setTimeout(() => {
            const pacContainers = document.querySelectorAll('.pac-container');
            pacContainers.forEach(container => {
              (container as HTMLElement).style.display = 'none';
            });
          }, 100);
        }
      });
    } catch (e: any) {
      setError(e.message || 'Autocomplete init failed');
          }
  }, [isLoaded, onChange, onAddressPicked]);
  
  const handleBlur = () => {
    if (inputRef.current) {
      onChange(inputRef.current.value);
        }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {error && (
        <div style={{ 
          position: 'absolute', 
          top: '-24px', 
          left: 0,
          right: 0,
          fontSize: '10px', 
          color: '#ff6b6b',
          fontFamily: 'monospace',
          background: 'rgba(0,0,0,0.8)',
          padding: '2px 4px',
          borderRadius: '2px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {error}
        </div>
      )}
      <input 
        ref={inputRef}
        defaultValue={value}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        style={{ 
          width: '100%',
          flex: 1, 
          minWidth: 160, 
          padding: 10, 
          borderRadius: 0, 
          border: error ? "2px solid #ff6b6b" : "1px solid #e5e7eb",
          fontSize: '16px',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
          ...(style || {})
        }}
      />
    </div>
  );
}
